import { useEffect, useState } from 'react'
import { apiHelper, validateGenericResponse } from '../helpers/webapi.helper'
import { AppContextType, AppRequest, ApiError } from '../types'
import { GAHelper } from '../helpers/ga.helper'

interface UseDataClientProps<O> {
    appContext: AppContextType | null,
    disableLoading?: boolean,
    initialTarget?: AppRequest<any, any, O>
}

interface ClientCallbackParams<O> {
    mappedData: O
}

type ClientCallback<O> = ({ mappedData }: ClientCallbackParams<O>) => void

export default function useDataClient<O>({ appContext, initialTarget, disableLoading }: UseDataClientProps<O>) {
    const [executeReq, setExecuteReq] = useState<boolean>(!!initialTarget)
    const [adapter, setAdapter] = useState<AppRequest<any, any, O> | undefined>(initialTarget)
    const [data, setData] = useState<O | null>()
    const [onResponse, setOnResponse] = useState<ClientCallback<O>>()
    const [error, setError] = useState<string | null>(null)

    function onError(err: ApiError) {
        setError(err.message)
        if (adapter?.hideError) {
            return
        }
        if (adapter?.endpoint.thirdParty && [412, 401].includes(err.status || 0)) {
            appContext?.notifyError(adapter?.endpoint.name || '', 'Usuario o contraseña incorrecta', false)
            return
        }
        let logout = !!(err.status && !adapter?.endpoint.thirdParty && err.status === 401)
        let title = logout ? 'Aviso' : (adapter?.endpoint.name || 'Aviso')
        let message = logout ? 'Sesión expirada' : err.message
        GAHelper.setServiceReject(err.message, adapter?.endpoint.url || '', appContext?.user.data?.user)
        appContext?.notifyError(title, message, !!logout)
    }

    function executeAdapter(adapterUpdated?: AppRequest<any, any, O>) {
        if (adapterUpdated) {
            setAdapter(adapterUpdated)
        }
        setExecuteReq(true)
    }


    function handleRequest({ target, onResponse }: { target: AppRequest<any, any, O>, onResponse: ClientCallback<O> }) {
        setAdapter(target)
        setOnResponse(onResponse)
        setExecuteReq(true)
    }

    function clean() {
        setData(null)
        setError(null)
        setOnResponse(undefined)
    }
    useEffect(() => {
        if (adapter && executeReq) {
            !disableLoading && appContext?.setLoading(true)
            if (!adapter.keepCacheBeforeNewRequest) {
                setData(null)
            }
            (adapter.dummy || apiHelper(adapter, appContext?.user.data?.token))
                .then((resp) => validateGenericResponse(adapter, resp))
                .then((mappedData) => {
                    setData(mappedData)
                    onResponse?.({mappedData})
                })
                .catch((err) => {
                    setData(null)
                    onError({ message: err.message, status: err.status || err.response?.status })
                })
                .finally(() => {
                    setExecuteReq(false)
                    !disableLoading && appContext?.setLoading(false)
                })
        }
    }, [adapter, executeReq])


    return {
        status: { data, error, loading: executeReq },
        executeRequest: executeAdapter,
        handleRequest,
        clean
    }
}

